import { useIntl } from "react-intl";
import React, { useEffect, useState } from "react";
import { Combobox, CurrencyInput, ETypeOfNotification, GetItemsProps, InputDate, Modal, RadioButtonGroup, setNotification, formatDateRange } from "medere-ui-framework";
import { Button } from "primereact/button";
import {NomenclatorDTO} from "../../dto/NomenclatorDTO";
import { ComboDataDTO, defaultAllComboDataDTO } from "../../dto/ComboDataDTO";
import { HonorariumDTO, defaultHonorariumDTO } from "../../dto/HonorariumDTO";
import { useDispatch } from 'react-redux';
import { AxiosResponse, HttpStatusCode } from "axios";
import "./Honorarium.scss";
import { useHonorariumService } from "../../servicesHooks/Honorarium/useHonorariumService";
import { useEntityService } from "../../servicesHooks/Entity/useEntityService";

const Actions = {
  CREATE: 1,
  UPDATE: 2,
  MODIFYCURRENT: 3
};

interface CRUHonorariumModalProps {
  openCreateHonorariumWindow: boolean;
  setOpenCreateHonorariumWindow: (open: boolean) => void;
  honorariumToEdit: HonorariumDTO;
  updateHonorariumTableFunction?:()=>void;
}

export default function CRUHonorariumModal({openCreateHonorariumWindow, setOpenCreateHonorariumWindow, honorariumToEdit,updateHonorariumTableFunction}: CRUHonorariumModalProps) {

  const dispatch = useDispatch();
  const { createHonorarium, modifyCurrentHonorarium, updateHonorarium } = useHonorariumService();
  const { getNomenclatorTypes, getNomenclators } = useEntityService();
  const intl = useIntl();
  const [isMobile] = useState(window.innerWidth <= 768);
  const [selectedNomenclator, setSelectedNomenclator] = useState<NomenclatorDTO | null>(null);
  const [selectedNomenclatorType, setSelectedNomenclatorType] = useState<ComboDataDTO | null>(null);
  const [selectedBillingByHour, setSelectedBillingByHour] = useState<ComboDataDTO | null>(null);
  const [fromDate, setFromDate] = useState<Date | null>(null);
  const [toDate, setToDate] = useState<Date | null>(null);
  const [newFromDate, setNewFromDate] = useState<Date | null>(null);
  const [newToDate, setNewToDate] = useState<Date | null>(null);
  const [ammount, setAmmount] = useState<number | null>(null);
  const [CRUAction, setCRUAction] = useState<number>(honorariumToEdit.honorarium != -1?Actions.MODIFYCURRENT:Actions.CREATE);
  const [currencyFocus, setCurrencyFocus] = useState<boolean>(false);
  const [minUpdateDate, setMinUpdateDate] = useState<Date | null>(null);

  const [showCurrencyContext, setShowCurrencyContext] = useState<boolean>(false);

  useEffect(() => { 
    setShowCurrencyContext((CRUAction != Actions.CREATE) && currencyFocus);
  }, [currencyFocus,CRUAction]);

  useEffect(() => { 
    setSelectedNomenclator((honorariumToEdit?.honorarium !== -1)?
      {nomenclator: honorariumToEdit.nomenclator,
      code: honorariumToEdit.nomenclatorcode,
      description: honorariumToEdit.nomenclatordesc,
      codeanddescription: honorariumToEdit.nomenclatorcode + ' - ' + honorariumToEdit.nomenclatordesc,
      nomenclatortype: honorariumToEdit.nomenclatortype, 
      nomenclatortypedesc: honorariumToEdit.nomenclatortypedesc,
      billingbyhour: honorariumToEdit.billingbyhour}:null);

    setSelectedNomenclatorType((honorariumToEdit?.honorarium !== -1)?{
      id: honorariumToEdit.nomenclatortype,
      name: honorariumToEdit.nomenclatortypedesc}:null);
    setSelectedBillingByHour(((honorariumToEdit?.honorarium !== -1)?{
      id: honorariumToEdit.billingbyhour?1:2,
      name: honorariumToEdit.billingbyhour?intl.formatMessage({ id: "ByHour" }):intl.formatMessage({ id: "ByAttention"})}:null));

    setFromDate(honorariumToEdit?.honorarium !== -1 ? honorariumToEdit.datestart : new Date());

    setToDate(honorariumToEdit?.honorarium !== -1 ? honorariumToEdit.dateend : null);

    setAmmount(honorariumToEdit?.honorarium !== -1 ? (honorariumToEdit.value != null ? honorariumToEdit.value : 0) : 0);

    setCRUAction(honorariumToEdit?.honorarium != -1?Actions.MODIFYCURRENT:Actions.CREATE);
    if(honorariumToEdit?.honorarium !== -1) {
      const newFromDateAndMinUpdateDate = new Date(honorariumToEdit.dateend ?? honorariumToEdit.datestart ?? new Date());
      newFromDateAndMinUpdateDate.setDate(newFromDateAndMinUpdateDate.getDate() + 1);
      setMinUpdateDate(newFromDateAndMinUpdateDate);
      setNewFromDate(newFromDateAndMinUpdateDate);
    }
  
    setNewToDate(null);
    setCurrencyFocus(false);
    
  }, [openCreateHonorariumWindow,honorariumToEdit.honorarium]);

  function createOrUpdateHonorarium(): void {
    const honorarium:HonorariumDTO = JSON.parse(JSON.stringify(honorariumToEdit?honorariumToEdit:defaultHonorariumDTO));
    if(validateHonorarium()){
      honorarium.billingbyhour = selectedBillingByHour?.id === 1;
      honorarium.nomenclator = selectedNomenclator?.nomenclator || null;
      honorarium.nomenclatortype = selectedNomenclatorType?.id || null;
      honorarium.datestart = fromDate;
      honorarium.dateend = toDate;
      honorarium.value = ammount;
      if (honorarium.honorarium !== -1) {
        if(CRUAction == Actions.MODIFYCURRENT) {
          modifyCurrentHonorarium(honorarium).then((res:AxiosResponse<HonorariumDTO> | void) => handleHonorariumResponse(res));
          return;
        }
        honorarium.datestart = newFromDate;
        honorarium.dateend = newToDate;
        updateHonorarium(honorarium).then((res:AxiosResponse<HonorariumDTO> | void) => handleHonorariumResponse(res));
        return;
      }
      createHonorarium(honorarium).then((res:AxiosResponse<HonorariumDTO> | void) => handleHonorariumResponse(res));
    }
  }

  function validateHonorarium(): boolean {

    if(!selectedNomenclator || selectedNomenclator.nomenclator=== -1) {
      dispatch(setNotification({typeOfNotification:ETypeOfNotification.ERROR, title:intl.formatMessage({ id: "BeCareful" }), desc:intl.formatMessage({ id: "NomenclatorIsMandatory" })}));
      return false;
    }
    if(!selectedBillingByHour || selectedBillingByHour.id === -1){
      dispatch(setNotification({typeOfNotification:ETypeOfNotification.ERROR, title:intl.formatMessage({ id: "BeCareful" }), desc:intl.formatMessage({ id: "TypeOfPaymentIsMandatory" })}));
      return false;
    }
    if(!fromDate) {
      dispatch(setNotification({typeOfNotification:ETypeOfNotification.ERROR, title:intl.formatMessage({ id: "BeCareful" }), desc:intl.formatMessage({ id: "ValidityFromIsMandatory" })}));
      return false;
    }
    if(toDate && toDate < fromDate){
      dispatch(setNotification({typeOfNotification:ETypeOfNotification.ERROR, title:intl.formatMessage({ id: "InvalidDates" }), desc:intl.formatMessage({ id: "TheValidityFromCanNotBeGreaterThanTo" })}));
      return false;
    }
    return true;
  }

  function handleHonorariumResponse(res:AxiosResponse<HonorariumDTO> | void): void {
    if(res){
      if(res.status === HttpStatusCode.Ok){
        setOpenCreateHonorariumWindow(false);
        updateHonorariumTableFunction?.();
      }else {
        dispatch(setNotification({typeOfNotification:ETypeOfNotification.ERROR, title:intl.formatMessage({ id: "OverlappingValidity" }), desc:`Verificar que la fecha ingresada no esté dentro del rango de fechas "${formatDateRange(res.data.datestart,res.data.dateend,intl.formatMessage({id:'NoLimit'}))}".`}))
      }  
    }
  }

  return (
    <Modal
      visible={openCreateHonorariumWindow}
      setVisible={setOpenCreateHonorariumWindow}
      className="custom-modal w-auto"
      header={<h6 className="modalTitle">{honorariumToEdit.honorarium!==-1?intl.formatMessage({ id: "EDIT" }):intl.formatMessage({ id: "CREATEHONORARIUM" })}</h6>}
    >
      <div style={{ minWidth: isMobile ? "" : "460px" }}>
        <Combobox 
                label={intl.formatMessage({id:'PracticeType'})} 
                getItems={(getItemProps:GetItemsProps)=> {
                    return getNomenclatorTypes(getItemProps).then((res:any)=>{
                        return [defaultAllComboDataDTO,...res];
                    })    
                }} 
                optionLabel='name' 
                setValue={(e:ComboDataDTO) => {
                    setSelectedNomenclatorType(e);
                }} 
                value={selectedNomenclatorType}
                placeholder={intl.formatMessage({id:'<All>'})} 
            />
        <Combobox
          label={intl.formatMessage({ id: "Practice" })}
          getItems={getNomenclators}
          params={{nomenclatorTypes:[selectedNomenclatorType?selectedNomenclatorType.id:-1] }}
          optionLabel="codeanddescription"
          minPanelWidth={600}
          maxPanelWidth={800}
          setValue={(e:NomenclatorDTO) => {
            if(e){
                if (e.nomenclatortypedesc !== undefined && e.nomenclatortype !== undefined) {
                    setSelectedNomenclatorType({name:e.nomenclatortypedesc,id:e.nomenclatortype});
                }
                setSelectedNomenclator(e);
            }
        }} 
        value={selectedNomenclator} 
        reLoadItemsValue={selectedNomenclatorType} 
        />
        <p style={{
          fontFamily: "Inter",
          fontSize: "11px",
          fontWeight: 400,
          lineHeight: "13px",
          letterSpacing: "0em",
        }}>
          {intl.formatMessage({ id: "InOrderToCreateANewNomenclatorCodeAddressBillingNomenclators" })}
        </p>
        <Combobox
          label={intl.formatMessage({ id: "Payment" })}
          list={[{id:1,name:intl.formatMessage({ id: "ByHour" })},{id:2,name:intl.formatMessage({ id: "ByAttention"})}]}
          optionLabel="name"
          minPanelWidth={400}
          maxPanelWidth={500}
          setValue={setSelectedBillingByHour}
          value={selectedBillingByHour}
        />
        <div style={showCurrencyContext?{background:'rgba(224, 168, 0, 0.1)', padding:'4px'}:{}}>
          <CurrencyInput className={'rounded-border'} label={intl.formatMessage({ id: "Ammount" })} ammount={ammount} setAmmount={setAmmount} onFocus={() => setCurrencyFocus(true)}/>
          {showCurrencyContext && 
          <React.Fragment>
            <RadioButtonGroup
              generalClassName="flex flex-column w-full"
              className="align-items-start md:align-items-start w-8 my-2"
              options={[{value:Actions.UPDATE, label:intl.formatMessage({ id: "UpdateWithNewValidity" })},{value:Actions.MODIFYCURRENT, label:intl.formatMessage({ id: "ModifyCurrent" })}]}
              orientation={"column"}
              itemWidth={50}
              label={intl.formatMessage({ id: "WhenChanginAmmoutYouAreGoingTo" })}
              value={CRUAction}
              setValue={(e) => {
                setCRUAction(e)
              }}            
            />
            {
              CRUAction == Actions.UPDATE && 
              <div>
                <p style={{
                  color:'rgba(224, 168, 0, 1)',
                  fontFamily: "Inter",
                  fontStyle: 'italic',
                  fontWeight: 400,
                  lineHeight: "16.94px"
                  }}>  
                    {intl.formatMessage({ id: "NewValidity" })} 
                    <span style={{fontSize:'11px'}}> ({intl.formatMessage({ id: "Mandatory" })})</span>
                  </p>
                <div className="flex md:gap-4 gap-2 flex-column md:flex-row">
                  <InputDate
                    value={newFromDate ? new Date(newFromDate) : null}
                    onChange={(e: any) => setNewFromDate(e.value)}
                    label={intl.formatMessage({ id: "From" })}
                    minDate={minUpdateDate ? new Date(minUpdateDate):null}
                  />
                  <InputDate
                    value={newToDate ? new Date(newToDate) : null}
                    onChange={(e: any) => setNewToDate(e.value)}
                    label={intl.formatMessage({ id: "to" })}
                    minDate={newFromDate ? new Date(newFromDate):null}
                  />
                </div>
              </div>
            }
          </React.Fragment>}
        </div>
        {/* 
        {(CRUAction == Actions.CREATE || !showCurrencyContext) && <CurrencyInput label={intl.formatMessage({ id: "Ammount" })} ammount={ammount} setAmmount={setAmmount} onFocus={() => {setShowCurrencyContext(true)}}/>} */}
        {CRUAction != Actions.UPDATE &&
        <div>
          <p className="label inputLabel undefined">
          {intl.formatMessage({ id: "Validity" })}
          </p>
          <div className="flex md:gap-4 gap-2 flex-column md:flex-column">
            <InputDate
              value={fromDate ? new Date(fromDate) : null}
              onChange={(e: any) => setFromDate(e.value)}
              label={intl.formatMessage({ id: "From" })}
            />
            <InputDate
              value={toDate ? new Date(toDate) : null}
              onChange={(e: any) => setToDate(e.value)}
              label={intl.formatMessage({ id: "to" })}
              minDate={fromDate ? new Date(fromDate):null}
            />
          </div>
        </div>}
        <br/>

        <Button
          className="buttonMain2 w-full" 
          label={intl.formatMessage({ id: "CancelM" })}
          onClick={() => setOpenCreateHonorariumWindow(false)}
        />
        
        <Button
          className="buttonMain w-full"
          label={CRUAction == Actions.UPDATE?intl.formatMessage({ id: "UPDATEWITHNEWVALIDITY" }):intl.formatMessage({ id: "SAVE" })}
          onClick={() => createOrUpdateHonorarium()}
        />
      </div>
    </Modal>
  );
}
